import { useEffect, useMemo, useState } from "react";
import { AdminLayout } from "@/components/AdminLayout";
import api, { API } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Award, Download, AlertTriangle, CheckCircle2, ListChecks, FileText, Search, Filter, Package, Loader2, Ban } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { UFS } from "@/lib/ufs";

const MONTHS = [
  { value: "1", label: "Janeiro" }, { value: "2", label: "Fevereiro" }, { value: "3", label: "Março" },
  { value: "4", label: "Abril" },   { value: "5", label: "Maio" },      { value: "6", label: "Junho" },
  { value: "7", label: "Julho" },   { value: "8", label: "Agosto" },    { value: "9", label: "Setembro" },
  { value: "10", label: "Outubro" },{ value: "11", label: "Novembro" }, { value: "12", label: "Dezembro" },
];

function creditClass(credits, required) {
  if ((credits || 0) >= (required || 3)) return "bg-emerald-100 text-emerald-800 border-emerald-200";
  return "bg-amber-100 text-amber-800 border-amber-200";
}

// Formata número de crédito com vírgula decimal (ex.: 2.5 -> "2,5")
const fmt = (n) => {
  const v = Number(n ?? 0);
  return (Number.isInteger(v) ? v.toFixed(1) : v.toString()).replace(".", ",");
};

export default function AdminCertificados() {
  const { user } = useAuth();
  const [tab, setTab] = useState("emit");

  // ===== Aba "Emitir" =====
  const [courses, setCourses] = useState([]);
  const [pickCourse, setPickCourse] = useState("");
  const [students, setStudents] = useState([]);
  const [courseInfo, setCourseInfo] = useState(null);
  const [creditsRequired, setCreditsRequired] = useState(3);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [filter, setFilter] = useState("");

  // ===== Filtros do seletor de curso (UF, mês, ano) =====
  // Persistidos entre sessões para o admin voltar e manter o contexto.
  const [ufFilter, setUfFilter] = useState(() => {
    try { return localStorage.getItem("ceap_cert_uf") || "todos"; } catch { return "todos"; }
  });
  const [monthFilter, setMonthFilter] = useState(() => {
    try { return localStorage.getItem("ceap_cert_month") || "todos"; } catch { return "todos"; }
  });
  const [yearFilter, setYearFilter] = useState(() => {
    try { return localStorage.getItem("ceap_cert_year") || "todos"; } catch { return "todos"; }
  });
  useEffect(() => { try { localStorage.setItem("ceap_cert_uf", ufFilter); } catch { /* ignore */ } }, [ufFilter]);
  useEffect(() => { try { localStorage.setItem("ceap_cert_month", monthFilter); } catch { /* ignore */ } }, [monthFilter]);
  useEffect(() => { try { localStorage.setItem("ceap_cert_year", yearFilter); } catch { /* ignore */ } }, [yearFilter]);

  // ===== Download em lote =====
  const [bulkDownloading, setBulkDownloading] = useState(false);

  // ===== Modal de emissão manual =====
  const [manualOpen, setManualOpen] = useState(false);
  const [manualTarget, setManualTarget] = useState(null);
  const [manualForm, setManualForm] = useState({ justification: "", manager_name: "" });
  const [manualSubmitting, setManualSubmitting] = useState(false);

  // ===== Modal de cancelamento de certificado =====
  const [cancelTarget, setCancelTarget] = useState(null); // { student }
  const [cancelReason, setCancelReason] = useState("");
  const [cancelSubmitting, setCancelSubmitting] = useState(false);

  // ===== Aba "Histórico" =====
  const [items, setItems] = useState([]);

  // Loads
  const loadCourses = () => api.get("/courses").then(r => setCourses(r.data || [])).catch(() => {});
  const loadHistory = () => api.get("/admin/certificates").then(r => setItems(r.data || [])).catch(() => {});
  const loadStudents = (courseId) => {
    if (!courseId) {
      setStudents([]);
      setCourseInfo(null);
      return;
    }
    setLoadingStudents(true);
    api.get(`/admin/courses/${courseId}/students`)
      .then(r => {
        setStudents(r.data?.students || []);
        setCourseInfo(r.data?.course || null);
        setCreditsRequired(r.data?.credits_required ?? 3);
      })
      .catch(err => {
        toast.error(err?.response?.data?.detail || "Erro ao carregar alunos do curso");
      })
      .finally(() => setLoadingStudents(false));
  };

  useEffect(() => { loadCourses(); loadHistory(); }, []);
  useEffect(() => { loadStudents(pickCourse); }, [pickCourse]);

  // ===== Filtros aplicados sobre a lista de cursos =====
  // Extrai anos únicos e UFs disponíveis (baseado em start_date de cada curso).
  const availableUFs = useMemo(() => {
    const set = new Set(courses.map(c => (c.estado || "").toUpperCase()).filter(Boolean));
    return UFS.filter(u => set.has(u.value));
  }, [courses]);
  const availableYears = useMemo(() => {
    const set = new Set(
      courses
        .map(c => (c.start_date || "").slice(0, 4))
        .filter(y => /^\d{4}$/.test(y))
    );
    return Array.from(set).sort((a, b) => b.localeCompare(a));
  }, [courses]);

  const filteredCoursesForSelect = useMemo(() => {
    return courses.filter(c => {
      if (ufFilter !== "todos" && (c.estado || "").toUpperCase() !== ufFilter) return false;
      const sd = c.start_date || "";
      if (yearFilter !== "todos" && sd.slice(0, 4) !== yearFilter) return false;
      if (monthFilter !== "todos") {
        const mm = sd.slice(5, 7);
        const numMM = String(parseInt(mm || "0", 10));
        if (numMM !== monthFilter) return false;
      }
      return true;
    });
  }, [courses, ufFilter, monthFilter, yearFilter]);

  // Se o curso selecionado saiu da lista filtrada, limpa
  useEffect(() => {
    if (pickCourse && !filteredCoursesForSelect.find(c => c.id === pickCourse)) {
      setPickCourse("");
    }
  }, [filteredCoursesForSelect, pickCourse]);

  const clearFilters = () => {
    setUfFilter("todos");
    setMonthFilter("todos");
    setYearFilter("todos");
  };

  // ===== Download em lote (ZIP com PDFs de todos aptos) =====
  const eligibleCount = students.filter(s => s.meets_requirement).length;

  const downloadBulkZip = async () => {
    if (!pickCourse || bulkDownloading) return;
    setBulkDownloading(true);
    try {
      const token = localStorage.getItem("ceap_token");
      const res = await fetch(
        `${API}/admin/certificates/course/${pickCourse}/bulk-zip`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        let msg = `Erro ${res.status}`;
        try { const j = await res.json(); msg = j.detail || msg; } catch { /* ignore parse */ }
        toast.error(msg);
        return;
      }
      const blob = await res.blob();
      const added = res.headers.get("X-Certs-Added") || "?";
      // Extrai nome do arquivo do Content-Disposition
      const cd = res.headers.get("Content-Disposition") || "";
      const match = cd.match(/filename="([^"]+)"/);
      const filename = match ? match[1] : `certificados_${pickCourse}.zip`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.rel = "noopener";
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      toast.success(`ZIP gerado com ${added} certificado(s)`);
      // Recarrega a lista de alunos para refletir novos certs emitidos
      loadStudents(pickCourse);
      loadHistory();
    } catch (err) {
      toast.error("Falha ao gerar ZIP: " + (err?.message || "erro desconhecido"));
    } finally {
      setBulkDownloading(false);
    }
  };

  // ===== Emissão direta (aluno tem frequência) =====
  const issueAuto = async (student) => {
    if (!pickCourse) return;
    try {
      await api.post("/admin/certificates/issue", {
        user_id: student.user_id,
        course_id: pickCourse,
      });
      toast.success(`Certificado emitido para ${student.full_name}`);
      loadStudents(pickCourse);
      loadHistory();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || "Erro ao emitir";
      // Item 4.5 — trava de duplicidade: 409 pede confirmação de substituição
      if (status === 409) {
        if (window.confirm(`${detail}\n\nDeseja SUBSTITUIR o certificado ativo por um novo?`)) {
          try {
            await api.post("/admin/certificates/issue", { user_id: student.user_id, course_id: pickCourse, replace: true });
            toast.success(`Certificado substituído para ${student.full_name}`);
            loadStudents(pickCourse);
            loadHistory();
          } catch (e2) {
            toast.error(e2?.response?.data?.detail || "Erro ao substituir certificado");
          }
        }
        return;
      }
      // Se frequência insuficiente, sugere emissão manual
      if (String(detail).toLowerCase().includes("frequência")) {
        toast.error(detail);
        openManual(student);
      } else {
        toast.error(detail);
      }
    }
  };

  // ===== Emissão manual (com justificativa) =====
  const openManual = (student) => {
    setManualTarget(student);
    setManualForm({
      justification: "",
      manager_name: user?.full_name || "",
    });
    setManualOpen(true);
  };

  const submitManual = async (e) => {
    e.preventDefault();
    if (!manualTarget || !pickCourse) return;
    const j = manualForm.justification.trim();
    const m = manualForm.manager_name.trim();
    if (j.length < 10) { toast.error("Justificativa precisa ter pelo menos 10 caracteres"); return; }
    if (!m) { toast.error("Informe o nome do gestor responsável"); return; }
    setManualSubmitting(true);
    try {
      await api.post("/admin/certificates/manual-issue", {
        user_id: manualTarget.user_id,
        course_id: pickCourse,
        justification: j,
        manager_name: m,
      });
      toast.success(`Certificado emitido manualmente para ${manualTarget.full_name}`);
      setManualOpen(false);
      setManualTarget(null);
      loadStudents(pickCourse);
      loadHistory();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || "Erro ao emitir manualmente";
      // Item 4.5 — trava de duplicidade: 409 pede confirmação de substituição
      if (status === 409) {
        if (window.confirm(`${detail}\n\nDeseja SUBSTITUIR o certificado ativo por um novo?`)) {
          try {
            await api.post("/admin/certificates/manual-issue", {
              user_id: manualTarget.user_id,
              course_id: pickCourse,
              justification: j,
              manager_name: m,
              replace: true,
            });
            toast.success(`Certificado substituído para ${manualTarget.full_name}`);
            setManualOpen(false);
            setManualTarget(null);
            loadStudents(pickCourse);
            loadHistory();
          } catch (e2) {
            toast.error(e2?.response?.data?.detail || "Erro ao substituir certificado");
          }
        }
      } else {
        toast.error(detail);
      }
    } finally {
      setManualSubmitting(false);
    }
  };

  const download = async (id, code) => {
    try {
      const token = localStorage.getItem("ceap_token");
      const res = await fetch(`${API}/certificates/${id}/download`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        toast.error(res.status === 403 ? "Aluno ainda não respondeu a pesquisa" : "Erro ao baixar");
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `certificado_${code}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // ago/2026 — adia revogação em 60s (browser precisa de tempo para processar).
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      toast.success("Certificado baixado");
    } catch { toast.error("Erro ao baixar"); }
  };

  // Cancela (revoga) um certificado emitido. Apenas Gestor Master.
  const submitCancel = async () => {
    if (!cancelTarget) return;
    const cert = cancelTarget.student?.certificate;
    if (!cert?.id) { setCancelTarget(null); return; }
    setCancelSubmitting(true);
    try {
      await api.post(`/admin/certificates/${cert.id}/cancel`, { reason: cancelReason.trim() || null });
      toast.success("Certificado cancelado");
      setCancelTarget(null);
      setCancelReason("");
      loadStudents(pickCourse);
      loadHistory();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao cancelar certificado");
    } finally {
      setCancelSubmitting(false);
    }
  };

  const filteredStudents = students.filter(s => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (s.full_name || "").toLowerCase().includes(q) ||
           (s.ceap_id || "").toLowerCase().includes(q) ||
           (s.email || "").toLowerCase().includes(q);
  });

  return (
    <AdminLayout title="Certificados">
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-2 w-full max-w-md mb-4">
          <TabsTrigger value="emit" data-testid="tab-emit"><Award className="w-4 h-4 mr-1" /> Emitir</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history"><ListChecks className="w-4 h-4 mr-1" /> Histórico</TabsTrigger>
        </TabsList>

        {/* ===== ABA: EMITIR ===== */}
        <TabsContent value="emit" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Filter className="w-4 h-4 text-[hsl(var(--accent))]" />
                1. Filtre e selecione o curso
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Linha de filtros: UF, Mês, Ano */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Estado (UF)</Label>
                  <Select value={ufFilter} onValueChange={setUfFilter}>
                    <SelectTrigger className="h-9" data-testid="cert-uf-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Todos os estados ({courses.length})</SelectItem>
                      {availableUFs.map((uf) => {
                        const count = courses.filter(c => (c.estado || "").toUpperCase() === uf.value).length;
                        return (
                          <SelectItem key={uf.value} value={uf.value}>
                            {uf.label} ({count})
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Mês</Label>
                  <Select value={monthFilter} onValueChange={setMonthFilter}>
                    <SelectTrigger className="h-9" data-testid="cert-month-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Todos os meses</SelectItem>
                      {MONTHS.map(m => (
                        <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Ano</Label>
                  <Select value={yearFilter} onValueChange={setYearFilter}>
                    <SelectTrigger className="h-9" data-testid="cert-year-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Todos os anos</SelectItem>
                      {availableYears.map(y => (
                        <SelectItem key={y} value={y}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {(ufFilter !== "todos" || monthFilter !== "todos" || yearFilter !== "todos") && (
                <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                  <span>Mostrando <strong>{filteredCoursesForSelect.length}</strong> de {courses.length} curso{courses.length !== 1 ? "s" : ""}.</span>
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="text-[hsl(var(--accent))] hover:underline"
                    data-testid="cert-clear-filters"
                  >
                    Limpar filtros
                  </button>
                </div>
              )}

              <Select value={pickCourse} onValueChange={setPickCourse}>
                <SelectTrigger data-testid="cert-course-select">
                  <SelectValue placeholder={
                    filteredCoursesForSelect.length === 0
                      ? "Nenhum curso corresponde aos filtros"
                      : "Escolha o curso para emitir certificados"
                  } />
                </SelectTrigger>
                <SelectContent className="max-h-[400px]">
                  {filteredCoursesForSelect.length === 0 ? (
                    <div className="px-3 py-4 text-xs text-muted-foreground text-center">
                      Ajuste os filtros acima para encontrar um curso.
                    </div>
                  ) : (
                    filteredCoursesForSelect.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.title} — {c.cidade}/{c.estado} ({c.workload_hours}h)
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          {pickCourse && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <span className="flex items-center gap-2">
                    2. Alunos com presença neste curso
                    <Badge variant="outline" className="text-xs">
                      Requisito: {fmt(creditsRequired)} créditos
                    </Badge>
                  </span>
                  <Button
                    onClick={downloadBulkZip}
                    disabled={bulkDownloading || eligibleCount === 0}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="cert-bulk-download"
                  >
                    {bulkDownloading ? (
                      <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Gerando ZIP…</>
                    ) : (
                      <><Package className="w-4 h-4 mr-1.5" /> Baixar todos aptos ({eligibleCount})</>
                    )}
                  </Button>
                </CardTitle>
                {eligibleCount > 0 && !bulkDownloading && (
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Gera um ZIP com o PDF de cada aluno que somou {fmt(creditsRequired)} créditos (turno pontual = 1,0; meia entrada = 0,5). Certificados ainda não emitidos serão criados automaticamente.
                  </p>
                )}
              </CardHeader>
              <CardContent>
                <div className="relative mb-3">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    className="pl-9"
                    placeholder="Filtrar por nome, código CEAP ou e-mail…"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    data-testid="cert-student-filter"
                  />
                </div>

                {loadingStudents ? (
                  <p className="text-sm text-muted-foreground py-8 text-center">Carregando alunos…</p>
                ) : filteredStudents.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-8 text-center">
                    {students.length === 0
                      ? "Nenhum aluno vinculado a este curso ainda (por presença ou matrícula)."
                      : "Nenhum aluno corresponde ao filtro."}
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Aluno</TableHead>
                          <TableHead className="text-center">Check-ins</TableHead>
                          <TableHead className="text-center">Créditos</TableHead>
                          <TableHead className="text-center">Status</TableHead>
                          <TableHead className="text-right">Ação</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredStudents.map(s => (
                          <TableRow key={s.user_id} data-testid={`cert-student-${s.user_id}`}>
                            <TableCell>
                              <div className="font-medium flex items-center gap-2">
                                {s.full_name}
                                {s.source === "matricula" && (
                                  <Badge variant="secondary" className="text-[9px]" data-testid={`cert-source-${s.user_id}`}>Matrícula</Badge>
                                )}
                              </div>
                              <div className="text-[11px] text-muted-foreground">{s.ceap_id} · {s.email}</div>
                            </TableCell>
                            <TableCell className="text-center">{s.sessions}</TableCell>
                            <TableCell className="text-center">
                              <Badge variant="outline" className={creditClass(s.credits, s.credits_required)} data-testid={`cert-credits-${s.user_id}`}>
                                {fmt(s.credits ?? 0)}/{fmt(s.credits_required ?? creditsRequired)}
                              </Badge>
                              {s.late_periods > 0 && (
                                <div className="text-[10px] text-amber-700 mt-0.5">{s.late_periods} meia(s) entrada(s)</div>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              {s.certificate_status === "cancelado" ? (
                                <Badge variant="outline" className="bg-rose-50 text-rose-800 border-rose-200" data-testid={`cert-status-cancelled-${s.user_id}`}>
                                  <Ban className="w-3 h-3 mr-1" /> Cancelado
                                </Badge>
                              ) : s.has_certificate ? (
                                <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">
                                  <CheckCircle2 className="w-3 h-3 mr-1" /> Já emitido
                                </Badge>
                              ) : s.meets_requirement ? (
                                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                  Apto
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200">
                                  <AlertTriangle className="w-3 h-3 mr-1" /> Faltam créditos
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              {s.certificate_status === "cancelado" ? (
                                <span className="text-[11px] text-rose-700">Revogado pelo Gestor Master</span>
                              ) : s.has_certificate ? (
                                <div className="flex items-center justify-end gap-1">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => download(s.certificate.id, s.certificate.validation_code)}
                                    data-testid={`cert-download-${s.user_id}`}
                                  >
                                    <Download className="w-4 h-4 mr-1" /> Baixar
                                  </Button>
                                  {user?.role === "admin_master" && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="text-rose-700 hover:bg-rose-50 hover:text-rose-800"
                                      onClick={() => { setCancelReason(""); setCancelTarget({ student: s }); }}
                                      data-testid={`cert-cancel-${s.user_id}`}
                                    >
                                      <Ban className="w-4 h-4 mr-1" /> Cancelar
                                    </Button>
                                  )}
                                </div>
                              ) : s.meets_requirement ? (
                                <Button
                                  size="sm"
                                  onClick={() => issueAuto(s)}
                                  data-testid={`cert-issue-${s.user_id}`}
                                >
                                  <Award className="w-4 h-4 mr-1" /> Emitir
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openManual(s)}
                                  className="border-amber-300 text-amber-800 hover:bg-amber-50"
                                  data-testid={`cert-manual-${s.user_id}`}
                                >
                                  <FileText className="w-4 h-4 mr-1" /> Emissão manual
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ===== ABA: HISTÓRICO ===== */}
        <TabsContent value="history">
          <Card className="rounded-xl">
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Aluno</TableHead>
                    <TableHead>Curso</TableHead>
                    <TableHead>Carga</TableHead>
                    <TableHead>Cidade</TableHead>
                    <TableHead>Data</TableHead>
                    <TableHead>Código</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map(c => (
                    <TableRow key={c.id}>
                      <TableCell className="font-medium">{c.student_name}</TableCell>
                      <TableCell>{c.course_name}</TableCell>
                      <TableCell>{c.course_hours}h</TableCell>
                      <TableCell>{c.course_city}</TableCell>
                      <TableCell>{c.course_date}</TableCell>
                      <TableCell className="font-mono-id text-xs">{c.validation_code}</TableCell>
                      <TableCell>
                        {c.is_manual_issue ? (
                          <Badge className="bg-amber-100 text-amber-800 border-amber-200" title={`Justificativa: ${c.manual_justification || ""}\nGestor: ${c.manual_manager_name || ""}`}>
                            <FileText className="w-3 h-3 mr-1" /> Manual
                          </Badge>
                        ) : (
                          <Badge variant="outline">Automática</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" onClick={() => download(c.id, c.validation_code)} data-testid={`cert-history-download-${c.id}`}>
                          <Download className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {items.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} className="py-10 text-center text-sm text-muted-foreground">
                        Nenhum certificado emitido ainda
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modal Emissão Manual */}
      <Dialog open={manualOpen} onOpenChange={(v) => !v && setManualOpen(false)}>
        <DialogContent className="max-w-[560px] rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" /> Emissão manual de certificado
            </DialogTitle>
            <DialogDescription>
              Este aluno ainda não atingiu os {fmt(creditsRequired)} créditos exigidos (turno pontual = 1,0; meia entrada = 0,5). A emissão manual exige justificativa e nome do gestor responsável.
            </DialogDescription>
          </DialogHeader>
          {manualTarget && (
            <form onSubmit={submitManual} className="space-y-3">
              <Card className="bg-amber-50 border-amber-200">
                <CardContent className="p-3 text-sm space-y-1">
                  <p><strong>Aluno:</strong> {manualTarget.full_name} ({manualTarget.ceap_id})</p>
                  <p><strong>Curso:</strong> {courseInfo?.title}</p>
                  <p>
                    <strong>Presença registrada:</strong> {fmt(manualTarget.credits ?? 0)}/{fmt(manualTarget.credits_required ?? creditsRequired)} créditos — {manualTarget.sessions} check-in(s){manualTarget.late_periods > 0 ? `, ${manualTarget.late_periods} meia(s) entrada(s)` : ""}
                  </p>
                </CardContent>
              </Card>
              <div>
                <Label>Justificativa da emissão manual *</Label>
                <Textarea
                  rows={3}
                  required
                  minLength={10}
                  value={manualForm.justification}
                  onChange={(e) => setManualForm(f => ({ ...f, justification: e.target.value }))}
                  placeholder="Ex: Aluno presente no curso mas esqueceu de bater check-out no segundo dia. Confirmei presença com a equipe pedagógica."
                  data-testid="manual-justification"
                />
                <p className="text-[10px] text-muted-foreground mt-1">Mínimo 10 caracteres. Ficará registrado na auditoria do sistema.</p>
              </div>
              <div>
                <Label>Nome do gestor responsável *</Label>
                <Input
                  required
                  value={manualForm.manager_name}
                  onChange={(e) => setManualForm(f => ({ ...f, manager_name: e.target.value }))}
                  placeholder="Seu nome completo"
                  data-testid="manual-manager"
                />
              </div>
              <p className="text-[11px] text-muted-foreground italic">
                ℹ️ A justificativa e o nome do gestor ficam registrados na <strong>auditoria do master</strong>, não no histórico do aluno.
              </p>
              <Button type="submit" className="w-full bg-amber-600 hover:bg-amber-700 text-white" disabled={manualSubmitting} data-testid="manual-submit">
                <Award className="w-4 h-4 mr-2" />
                {manualSubmitting ? "Emitindo…" : "Confirmar emissão manual"}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== Modal de CANCELAMENTO (revogação) de certificado ===== */}
      <Dialog open={!!cancelTarget} onOpenChange={(open) => { if (!open) { setCancelTarget(null); setCancelReason(""); } }}>
        <DialogContent className="max-w-[460px] rounded-2xl" data-testid="cert-cancel-dialog">
          <DialogHeader>
            <div className="w-12 h-12 rounded-xl bg-rose-100 text-rose-700 flex items-center justify-center mb-1">
              <Ban className="w-6 h-6" />
            </div>
            <DialogTitle className="font-display text-lg">Cancelar certificado</DialogTitle>
            <DialogDescription className="text-sm leading-relaxed">
              O certificado será <strong>revogado</strong>: o aluno perde o acesso, a validação pública fica inválida e o sistema <strong>não reemite</strong> automaticamente. Esta ação fica registrada na auditoria.
            </DialogDescription>
          </DialogHeader>
          {cancelTarget?.student && (
            <div className="px-3 py-2 rounded-lg bg-[hsl(var(--muted))]/40 border border-[hsl(var(--border))] text-sm space-y-0.5">
              <p><strong>Aluno:</strong> {cancelTarget.student.full_name} ({cancelTarget.student.ceap_id})</p>
              <p><strong>Curso:</strong> {courseInfo?.title}</p>
              {cancelTarget.student.certificate?.validation_code && (
                <p className="text-[11px] font-mono-id text-muted-foreground">Código: {cancelTarget.student.certificate.validation_code}</p>
              )}
            </div>
          )}
          <div>
            <Label>Motivo do cancelamento (opcional)</Label>
            <Textarea
              rows={3}
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Ex: Certificado emitido por engano — presença lançada no curso errado."
              data-testid="cert-cancel-reason"
            />
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2 mt-1">
            <Button variant="outline" className="flex-1" onClick={() => { setCancelTarget(null); setCancelReason(""); }} data-testid="cert-cancel-abort">
              Voltar
            </Button>
            <Button
              className="flex-1 bg-rose-600 hover:bg-rose-700 text-white"
              onClick={submitCancel}
              disabled={cancelSubmitting}
              data-testid="cert-cancel-confirm"
            >
              <Ban className="w-4 h-4 mr-1" /> {cancelSubmitting ? "Cancelando…" : "Confirmar cancelamento"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
